<template>
    <div class="card" style="min-height: 200px">
        <div class="bg-blue-light p-1">
            <TitleButton
                btnTitle="Add New"
                :showBtn="false"
                :showAddNew="true"
                title="Hotel Invoice list"
                @onClickAddNewButton="navigateToAddPage"
            />
        </div>

        <div class="my-2 px-2">
            <DateQuerySetter btnTitle="Go" @onClickGo="onClickGo"/>
        </div>

        <div class="col-12 px-2 mt-2 table-responsive action-table-responsive">
            <table class="table">
                <thead>
                    <tr>
                        <th>Invoice No</th>
                        <th>Invoice Date</th>
                        <th>Guest</th>
                        <th>Room</th>
                        <th class="text-end">Bill Amount</th>
                        <th>Status</th>
                        <th>Action</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(item, i) in billList" :key="i">
                        <td>{{ item.bill_number }}</td>
                        <td>{{ item.date }}</td>
                        <td>{{ item.party_name }}</td>
                        <td>{{ roomName(item) }}</td>
                        <td class="text-end">{{ item.bill_amount }}</td>
                        <td>{{ item.status }}</td>
                        <td>
                            <div class="d-flex">
                                <div class="dropdown">
                                    <button class="btn btn-secondary btn-sm dropdown-toggle" type="button" data-bs-toggle="dropdown" data-bs-boundary="viewport">
                                        <i class="fas fa-ellipsis-v"></i>
                                    </button>
                                    <ul class="dropdown-menu dropdown-menu-end action-dropdown-menu">
                                        <li>
                                            <a class="dropdown-item" href="#" @click.prevent="goToPrint(item.id)">
                                                <i class="fas fa-print me-2"></i> Print Invoice
                                            </a>
                                        </li>
                                        <li v-if="item.status !== 'returned'">
                                            <a class="dropdown-item" href="#" @click.prevent="goToEdit(item.id)">
                                                <i class="fas fa-pencil me-2"></i> Edit Invoice
                                            </a>
                                        </li>
                                        <li v-if="item.status === 'active'">
                                            <a class="dropdown-item" href="#" @click.prevent="goToReturn(item.id)">
                                                <i class="far fa-window-close me-2"></i> Return Invoice
                                            </a>
                                        </li>
                                        <li v-if="item.status !== 'returned'">
                                            <a class="dropdown-item" href="#" @click.prevent="goToPartialReturn(item.id)">
                                                <i class="fas fa-undo-alt me-2"></i> Partial Return Invoice
                                            </a>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </td>
                    </tr>
                    <tr v-if="!billList.length">
                        <td colspan="7" class="text-center">No data</td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div class="px-2">
            <Pagination :offset="offset" @onPageChange="onPageChange"/>
        </div>
    </div>
</template>

<script setup>
import TitleButton from '@/components/atom/TitleButton';
import DateQuerySetter from '@/components/atom/DateQuerySetter';
import Pagination from '@/components/atom/Pagination';
import { onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import handlePurchase from '@/services/modules/purchase';
import handleCompany from '@/services/modules/company';
import hospitalPdfPrinterHelper from '@/services/utils/hospitalPdfPrinterHelper';

const props = defineProps({
    reopenCheckoutId: {
        type: Number,
        default: null,
    },
});

const $route = useRoute();
const $router = useRouter();
const $store = useStore();
const { fetchSalesList, fetchSalesBill } = handlePurchase();
const { fetchCompanyInfo } = handleCompany();
const { generatePdf } = hospitalPdfPrinterHelper();

const offset = ref(20);
const billList = ref([]);
const companyInfo = ref({});

const getRegisterId = () => {
    return $route.params.ipdRegisterId || $route.query.ipd_register_id;
};

const getQuery = () => {
    let query = `?company_id=${$route.params.companyId}`;
    query += `&ipd_register_id=${getRegisterId()}`;
    query += '&exclude_sale_type=ipd_pharmacy_invoice';
    if ($route.query.page) query += '&page=' + $route.query.page;
    query += '&offset=' + offset.value;
    return query;
};

const onClickGo = () => {
    fetchSalesList(getQuery()).then(res => {
        if (! res.status) {
            billList.value = [];
            $store.commit('resetPagination');
            return;
        }
        billList.value = res.date.data;
        setPagination(res.date);
    });
};

const setPagination = (data) => {
    if (!data.data.length) return;
    $store.commit('setPaginationData', {
        records: data.total,
        perPage: data.per_page,
        currentPage: data.current_page,
    });
};

const roomName = (item) => {
    if (!item.ipd_register) return '';
    if (!item.ipd_register.service_resource) return '';
    return item.ipd_register.service_resource.name;
};

const buildBackUrl = () => {
    if (!props.reopenCheckoutId) return $route.fullPath;
    const backQuery = Object.assign({}, $route.query, {
        tab: 'pending_checkout',
        reopen_checkout: props.reopenCheckoutId,
    });
    const search = new URLSearchParams(backQuery).toString();
    return `${$route.path}?${search}`;
};

const navigateToAddPage = () => {
    const query = Object.assign({}, $route.query, {
        back_url: encodeURIComponent(buildBackUrl()),
        bill_title: 'Create Hotel Bill',
    });
    $router.push({
        name: 'ipd-bill-add',
        params: Object.assign({}, $route.params, { ipdRegisterId: getRegisterId() }),
        query: query,
    });
};

const goToEdit = (ipdId) => {
    const query = Object.assign({}, $route.query, {
        back_url: encodeURIComponent(buildBackUrl()),
        bill_title: 'Update Hotel Bill',
    });
    $router.push({
        name: 'ipd-bill-edit',
        params: {
            companyId: $route.params.companyId,
            moduleId: $route.params.moduleId,
            menuId: $route.params.menuId,
            pageId: $route.params.pageId,
            ipdId: ipdId,
            ipdRegisterId: getRegisterId(),
        },
        query: query,
    });
};

const goToPrint = (id) => {
    const companyQuery = `?company_id=${$route.params.companyId}`;
    fetchSalesBill(id, companyQuery).then(res => {
        if (! res.status) return;
        generatePdf(companyInfo.value, res.data, 'Hotel Bill', false, true);
    });
};

const buildReturnQuery = () => {
    if (!props.reopenCheckoutId) {
        return {
            tab: 'ipd-bill',
            sale_type: 'ipd_invoice',
            redirect_route: 'hotel-bill-statement',
        };
    }
    return {
        tab: 'pending_checkout',
        sale_type: 'ipd_invoice',
        redirect_route: 'hotel-frontdesk',
        reopen_checkout: props.reopenCheckoutId,
    };
};

const pushReturnRoute = (routeName, id) => {
    const params = {
        companyId: $route.params.companyId,
        moduleId: $route.params.moduleId,
        menuId: $route.params.menuId,
        pageId: $route.params.pageId,
        ipdRegisterId: getRegisterId(),
        invoiceId: id,
    };
    const query = Object.assign({}, $route.query, buildReturnQuery());
    $router.push({ name: routeName, params: params, query: query });
};

const goToReturn = (id) => {
    pushReturnRoute('hotel-bill-return', id);
};

const goToPartialReturn = (id) => {
    pushReturnRoute('hotel-bill-partial-return', id);
};

const onPageChange = async (page) => {
    const routeQuery = Object.assign({}, $route.query, { page });
    await $router.push({ path: $route.path, query: routeQuery });
    onClickGo();
};

onMounted(() => {
    fetchCompanyInfo($route.params.companyId).then(res => {
        if (! res.data) return;
        companyInfo.value = res.data;
    });
});

onClickGo();
</script>

<style scoped>
.action-table-responsive {
    overflow: visible;
}
.action-dropdown-menu {
    position: absolute;
    z-index: 1055;
}
</style>
